import { useState, useEffect, useCallback } from 'react';
import { collection, query, where, onSnapshot } from 'firebase/firestore';
import { db } from '../services/firebase';
import { useAuth } from './useAuth';
import { addApiKey as addApiKeyService, deleteApiKey as deleteApiKeyService } from '../services/api';
import { ApiKeyType } from '../types';

interface ApiKey {
  keyId: string;
  keyType: ApiKeyType;
  nickname: string;
  isDefault: boolean;
  createdAt: Date;
  lastUsedAt?: Date;
}

/**
 * Hook to fetch and manage user's API keys
 * Uses real-time Firestore listener
 */
export const useApiKeys = () => {
  const { user } = useAuth();
  const [keys, setKeys] = useState<ApiKey[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    if (!user) {
      setKeys([]);
      setLoading(false);
      return;
    }

    try {
      const q = query(
        collection(db, 'apiKeys'),
        where('userId', '==', user.uid)
        // Note: orderBy removed temporarily while index builds
        // Will be re-enabled once Firestore index is ready
        // orderBy('createdAt', 'desc')
      );

      const unsubscribe = onSnapshot(
        q,
        (snapshot) => {
          const apiKeys = snapshot.docs.map((doc) => {
            const data = doc.data();
            return {
              keyId: doc.id,
              keyType: data.keyType || 'claude',
              nickname: data.nickname,
              isDefault: data.isDefault || false,
              createdAt: data.createdAt?.toDate() || new Date(),
              lastUsedAt: data.lastUsedAt?.toDate(),
            };
          });

          setKeys(apiKeys);
          setLoading(false);
          setError(null);
        },
        (err) => {
          console.error('Error fetching API keys:', err);
          setError(err as Error);
          setLoading(false);
        }
      );

      return () => unsubscribe();
    } catch (err) {
      console.error('Error setting up API keys listener:', err);
      setError(err as Error);
      setLoading(false);
    }
  }, [user]);

  return { keys, loading, error };
};

/**
 * Hook to add a new API key
 */
export const useAddApiKey = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const addKey = useCallback(async (apiKey: string, keyType: ApiKeyType, nickname: string, isDefault: boolean) => {
    setLoading(true);
    setError(null);

    try {
      await addApiKeyService({ apiKey, keyType, nickname, isDefault });
      setLoading(false);
    } catch (err) {
      console.error('Error adding API key:', err);
      const errorMessage = err instanceof Error ? err.message : 'Failed to add API key';
      setError(new Error(errorMessage));
      setLoading(false);
      throw err;
    }
  }, []);

  return { addKey, loading, error };
};

/**
 * Hook to delete an API key
 */
export const useDeleteApiKey = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const deleteKey = useCallback(async (keyId: string) => {
    setLoading(true);
    setError(null);

    try {
      await deleteApiKeyService(keyId);
      setLoading(false);
    } catch (err) {
      console.error('Error deleting API key:', err);
      const errorMessage = err instanceof Error ? err.message : 'Failed to delete API key';
      setError(new Error(errorMessage));
      setLoading(false);
      throw err;
    }
  }, []);

  return { deleteKey, loading, error };
};
